<script lang="ts">

import PageArticle from "./pageArticle.vue";
import {useLandingPageStore} from "../../../stores/landingPage";
import {useAnalyticEventStore} from "../../../stores/analyticEvents";
import {useOrganizationStore} from "../../../stores/organizations";

export default {
  name: "AddSectionModal",
  components: {PageArticle},
  emits: ['close'],
  setup(){
    const landingStore = useLandingPageStore();
    const analyticEventStore = useAnalyticEventStore();
    const organizationStore = useOrganizationStore();

    return {landingStore, analyticEventStore, organizationStore};
  },
  data(){
    return {
      componentToAdd: {},
      selectedType: 'article',
    };
  },
  computed: {
    brand() {
      return this.landingStore.localBrand;
    },
  },
  mounted(){
    this.componentToAdd = this.defaultValues('article');
  },
  methods: {
    defaultValues(type){
      switch (type) {
        case 'article':
          return {
            component_type: 'article',
            component_data: {
              title: {
                label: "Header",
                placeholder: "New Content Section Title",
                type: "text",
              },
              bodyText: {
                label: "Body Text",
                placeholder: "Add description text here",
                rows: 5,
                type: "rich-text",
              },
              layout: {
                label: "Layout",
                options: {
                  imageLeft: "Image Left",
                  imageRight: "Image Right",
                },
                type: "select",
              },
              articleImage: {
                resourceId: null,
                label: "Image",
                ratio: 1,
                type: "image",
              },
            }
          };
        //Commenting out - We don't want to add another grid. Eventually we may highlight a fund/campaign.
        // case 'donatable_grid':
        //   return {
        //     component_type: 'donatable_grid',
        //     component_data: {
        //       title: {
        //         type: "text",
        //         label: "Title",
        //         placeholder: "Ways to Give",
        //       },
        //       layout: {
        //         label: "Card Layout",
        //         options: {
        //           grid: "Grid",
        //           list: "List",
        //           carousel: "Carousel",
        //         },
        //         type: "select",
        //       },
        //       order: {
        //         label: "Order",
        //         type: "campaign-sort-order",
        //       },
        //     },
        //   }
        case 'video':
          return {
            component_type: 'video',
            component_data: {
              title: {
                label: "Header",
                placeholder: "Title",
                type: "text",
              },
              videoId: {
                label: "Video Id",
                type: "text",
                placeholder: "xxxxxxxxxxx"
              }
            }
          };
        default:
          return {};
      }
    },
    addComponent() {
      const newComponent = JSON.parse(JSON.stringify(this.defaultValues(this.selectedType)));
      this.landingStore.addComponentToDraft(newComponent);
      this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
      // TODO: Add analytics event for section type added (once we increase the components that can be added)
      this.analyticEventStore.logEvent({ action: `builder-add-component` });
      this.$emit('close');
      this.landingStore.saveCurrentDraft();
    }
  }
};

</script>
<template>
  <div class="flex flex-col">
    <div class="flex flex-row gap-4">
      <div class="flex flex-col">
        <h1 class="heading">
          Add a New Section
        </h1>
        <div class="component-selector">
          <label>Section Type</label>
          <select v-model="selectedType">
            <option value="article">
              Content Section
            </option>
            <option value="video">
              Video
            </option>
          </select>
        </div>
      </div>
    </div>
    <hr class="separator">
    <p class="preview-label">
      Preview
    </p>
    <div class="component-select-container">
      <div
        v-if="selectedType === 'article'"
        class="article-preview"
      >
        <div class="image-placeholder">
          <i class="fa-regular fa-image" />
        </div>
        <page-article
          :layout="componentToAdd.component_data?.layout"
          :title="componentToAdd.component_data?.title.placeholder"
          :body-text="componentToAdd.component_data?.bodyText.placeholder"
        />
      </div>
      <div
        v-if="selectedType === 'video'"
        class="video-preview"
      >
        <h2 class="video-preview-title">
          Video Title
        </h2>
        <p class="video-preview-subtitle">
          Video Subtitle
        </p>
        <div class="video-placeholder">
          <i class="fa-regular fa-circle-play" />
        </div>
      </div>
    </div>
  </div>
  <div class="add-button">
    <button
      class="btn-filled"
      @click="addComponent"
    >
      Add Section
    </button>
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/_variables.scss';

.heading {
  font-size: $section-subheading-size;
  font-weight: $section-subheading-weight;
  line-height: $section-subheading-line;
}

.component-selector {
  display: flex;
  align-items: center;
  gap: $space-md;
  margin-top: $space-md;

  label {
    margin: 0;
    font-size: $section-body-size;
    color: $color-neutral-700;
  }

  select {
    padding: $space-sm $space-md;
    border: 1px solid $color-neutral-200;
    border-radius: $radius-soft;
    background-color: white;
    font-size: $section-body-size;
    min-width: 150px;
  }
}

.separator {
  margin: $space-lg 0;
  border-color: $color-neutral-100;
}

.preview-label {
  font-size: $section-body-size;
  color: $color-neutral-600;
  margin-bottom: $space-sm;
}

.component-select-container {
  border: 2px dashed $color-neutral-300;
  border-radius: $radius-rounded;
  overflow: hidden;
  background-color: $color-neutral-50;
  padding: $space-md;
}

.article-preview {
  display: flex;
  gap: $space-lg;

  .image-placeholder {
    width: 33%;
    min-height: 120px;
    flex-shrink: 0;
    background-color: $color-neutral-200;
    border-radius: $radius-rounded;
    display: flex;
    align-items: center;
    justify-content: center;

    i {
      font-size: 2rem;
      color: $color-neutral-400;
    }
  }

  :deep(.outer-container) {
    background: transparent;
    width: auto;
  }

  :deep(#article-container) {
    box-shadow: none;
    padding: 0;
    background: transparent;
    border-radius: 0;
  }

  :deep(.inner-container) {
    padding: 0 !important;
    margin: 0 !important;
    max-width: none;
  }
}

.add-button {
  display: flex;
  justify-content: flex-end;
  margin-top: $space-lg;
}

.video-preview {
  text-align: center;

  .video-preview-title {
    font-size: $section-subheading-size;
    font-weight: $section-subheading-weight;
    line-height: $section-subheading-line;
    margin-bottom: $space-xs;
  }

  .video-preview-subtitle {
    font-size: $section-body-size;
    color: $color-neutral-600;
    margin-bottom: $space-lg;
  }

  .video-placeholder {
    width: 60%;
    aspect-ratio: 16 / 9;
    margin: 0 auto;
    background-color: $color-neutral-200;
    border-radius: $radius-rounded;
    display: flex;
    align-items: center;
    justify-content: center;

    i {
      font-size: 3rem;
      color: $color-neutral-400;
    }
  }
}
</style>
